import { CurrentStep } from '../enums';

/**
 * FTO 检索模拟配置接口
 */
export interface FtoSimulationConfig {
    /**
     * 搜索轮次配置数组
     */
    searchRounds: Array<{
        /** 检索式 */
        query: string;
        /** 是否手动输入，默认 false */
        isManual?: boolean;
        /** 目标执行步骤，默认执行到最后 (CurrentStep.Final) */
        targetStep?: CurrentStep;
        /** 自定义步骤延迟时间（毫秒），会覆盖全局配置 */
        customStepDelays?: Partial<Record<CurrentStep, number>>;
        /** 是否在项目开始时自动执行，默认 true */
        autoExecute?: boolean;
    }>;

    /**
     * 执行模式
     * - 'sequential': 串行执行（一个轮次完成后再执行下一个）
     * - 'parallel': 并行执行（所有轮次同时开始）
     */
    executionMode?: 'sequential' | 'parallel';

    /**
     * 全局步骤延迟时间配置（毫秒）
     */
    globalStepDelays?: Partial<Record<CurrentStep, number>>;

    /**
     * 错误处理策略
     * - 'stop': 遇到错误立即停止所有执行
     * - 'continue': 继续执行其他轮次
     * - 'retry': 重试失败的轮次
     */
    onError?: 'stop' | 'continue' | 'retry';

    /**
     * 最大重试次数，仅在 onError 为 'retry' 时有效
     */
    maxRetries?: number;

    /**
     * 是否在开始前清空现有数据，默认 true
     */
    clearBeforeStart?: boolean;

    /**
     * 回调函数配置
     */
    callbacks?: {
        /** 搜索轮次开始执行时的回调 */
        onRoundStart?: (roundIndex: number, query: string) => void;
        /** 搜索轮次完成时的回调 */
        onRoundComplete?: (roundIndex: number, query: string, success: boolean) => void;
        /** 单个步骤完成时的回调 */
        onStepComplete?: (roundIndex: number, step: CurrentStep, patentCount: number) => void;
        /** 所有轮次执行完成时的回调 */
        onAllComplete?: (results: FtoExecutionResult[]) => void;
        /** 发生错误时的回调 */
        onError?: (error: Error, roundIndex: number, step?: number) => void;
        /** 进度更新回调 */
        onProgress?: (progress: {
            totalRounds: number;
            completedRounds: number;
            runningRounds: number;
            failedRounds: number;
        }) => void;
    };
}

/**
 * FTO 执行结果接口
 */
export interface FtoExecutionResult {
    /** 搜索轮次索引 */
    roundIndex: number;
    /** 检索式 */
    query: string;
    /** 是否成功完成 */
    success: boolean;
    /** 最终执行到的步骤 */
    finalStep: CurrentStep;
    /** 最终专利数量 */
    finalPatentCount: number;
    /** 执行耗时（毫秒） */
    executionTime: number;
    /** 错误信息（如果有） */
    error?: string;
    /** 每个步骤的详细信息 */
    stepDetails: Array<{
        step: CurrentStep;
        patentCount: number;
        duration: number;
        success: boolean;
    }>;
}

/**
 * 默认的模拟配置
 */
export const DEFAULT_SIMULATION_CONFIG: Partial<FtoSimulationConfig> = {
    executionMode: 'sequential',
    onError: 'continue',
    maxRetries: 1,
    clearBeforeStart: true,
    globalStepDelays: {
        [CurrentStep.Query]: 2000, // 查询步骤：2秒
        [CurrentStep.FilterStep1]: 3000, // R1：3秒
        [CurrentStep.FilterStep2]: 4000, // R2：4秒
        [CurrentStep.FilterStep3]: 5000, // R3：5秒
        [CurrentStep.Final]: 2000, // Final：2秒
    },
};

/**
 * 预设的模拟场景配置
 */
export const PRESET_SIMULATION_SCENARIOS = {
    /** 快速演示场景 - 所有步骤500ms */
    QUICK_DEMO: {
        executionMode: 'parallel' as const,
        globalStepDelays: {
            [CurrentStep.Query]: 500,
            [CurrentStep.FilterStep1]: 500,
            [CurrentStep.FilterStep2]: 500,
            [CurrentStep.FilterStep3]: 500,
            [CurrentStep.Final]: 500,
        },
    },

    /** 真实时间场景 - 模拟真实检索耗时 */
    REALISTIC_TIMING: {
        executionMode: 'sequential' as const,
        globalStepDelays: {
            [CurrentStep.Query]: 5000, // 查询：5秒
            [CurrentStep.FilterStep1]: 8000, // R1：8秒
            [CurrentStep.FilterStep2]: 12000, // R2：12秒
            [CurrentStep.FilterStep3]: 15000, // R3：15秒
            [CurrentStep.Final]: 3000, // Final：3秒
        },
    },

    /** 并行执行场景 - 多轮次同时进行 */
    PARALLEL_EXECUTION: {
        executionMode: 'parallel' as const,
        onError: 'continue' as const,
        globalStepDelays: {
            [CurrentStep.Query]: 3000,
            [CurrentStep.FilterStep1]: 4000,
            [CurrentStep.FilterStep2]: 5000,
            [CurrentStep.FilterStep3]: 6000,
            [CurrentStep.Final]: 2000,
        },
    },
} as const;
